// Workspace Switcher (Spec §5.1) — the single place a user changes context.
// Personal is always present; Organisation / Aggregator / Platform Admin appear
// only when identity authorises them. Selecting one re-renders the sidebar to
// that workspace's authorised nav and navigates to its home.
//
// Two shapes, one control (Story #5648 — the figures show both):
//   variant="topbar"  the chip beside the search box: "Meridian Ventures —
//                     Billing Owner"
//   variant="sidebar" the block under the logo: the workspace name over
//                     "Aggregator · Billing Owner", with the ⇅ affordance
// The role is a real membership row, never a purchase.

import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check, ChevronDown, ChevronsUpDown, Building2, LayoutGrid, Shield, User } from 'lucide-react'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import { useAggregator } from '../../contexts/AggregatorContext'
import { useAuth } from '../../contexts/AuthContext'
import { personalRoleLabel, workspaceChipLabel, workspaceIdentity, workspaceRoleLabel } from './workspaceIdentity'
import type { WorkspaceKind } from '../../config/navigation'

const KIND_ICON: Record<WorkspaceKind, typeof User> = {
  personal: User,
  organisation: Building2,
  aggregator: LayoutGrid,
  admin: Shield,
}

const KIND_HOME: Record<WorkspaceKind, string> = {
  personal: '/',
  organisation: '/organisation/members',
  aggregator: '/team',
  admin: '/admin',
}

// The top bar is white, so the chip uses DARK text on a light ground — the
// earlier light-text-on-translucent values rendered white-on-white (WCAG 1.0).
// Personal keeps the neutral pill; a dedicated workspace is a white chip with
// a small colour swatch, as the figures draw it. Amber is deliberately NOT
// used here: on these screens amber means opportunity geometry and the call to
// action, and a workspace chip is neither.
const KIND_CHIP: Record<WorkspaceKind, string> = {
  personal: 'bg-slate-100 text-slate-700',
  organisation: 'border border-slate-200 bg-white text-iflow-900',
  aggregator: 'border border-slate-200 bg-white text-iflow-900',
  admin: 'border border-slate-200 bg-white text-iflow-900',
}

/** The swatch that tells one dedicated workspace from another at a glance. */
const KIND_SWATCH: Record<WorkspaceKind, string> = {
  personal: '',
  organisation: 'bg-emerald-500',
  aggregator: 'bg-iflow-500',
  admin: 'bg-rose-500',
}

export interface WorkspaceSwitcherProps {
  variant?: 'topbar' | 'sidebar'
  /** sidebar only: icon-rail mode hides the words. */
  collapsed?: boolean
}

export default function WorkspaceSwitcher({ variant = 'topbar', collapsed = false }: WorkspaceSwitcherProps) {
  const { workspaces, active, setActiveWorkspace } = useWorkspace()
  const { memberships } = useAggregator()
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onEsc)
    }
  }, [])

  // With only Personal available there is nothing to switch to — show a static
  // chip rather than an inert dropdown (P7: no dead controls).
  const single = workspaces.length <= 1
  const ActiveIcon = KIND_ICON[active.kind]
  const chipLabel = workspaceChipLabel(active, memberships)
  const chipRole = workspaceRoleLabel(active, memberships)
  const identity = workspaceIdentity(active, memberships, user?.name, personalRoleLabel(user))

  function select(id: string, kind: WorkspaceKind) {
    setActiveWorkspace(id)
    setOpen(false)
    navigate(KIND_HOME[kind])
  }

  const menu = open && !single && (
    <div
      role="menu"
      className={`absolute left-0 z-50 mt-2 w-64 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 text-left shadow-xl ${
        variant === 'sidebar' ? 'top-full' : ''
      }`}
    >
      <p className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Switch workspace</p>
      {workspaces.map((w) => {
        const Icon = KIND_ICON[w.kind]
        const isActive = w.id === active.id
        return (
          <button
            key={w.id}
            type="button"
            role="menuitemradio"
            aria-checked={isActive}
            onClick={() => select(w.id, w.kind)}
            className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm text-slate-700 hover:bg-iflow-50"
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-iflow-100 text-iflow-700">
              <Icon size={16} aria-hidden />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium text-iflow-900">{w.name}</span>
              <span className="block text-xs capitalize text-slate-400">{w.kind === 'admin' ? 'platform admin' : w.kind}</span>
            </span>
            {isActive && <Check size={16} className="text-iflow-600" aria-hidden />}
          </button>
        )
      })}
    </div>
  )

  if (variant === 'sidebar') {
    return (
      <div className="relative mx-3 mb-3 mt-0.5" ref={ref}>
        <button
          type="button"
          onClick={() => !single && setOpen((o) => !o)}
          aria-haspopup={single ? undefined : 'menu'}
          aria-expanded={single ? undefined : open}
          disabled={single}
          title={chipLabel}
          className={`flex w-full items-center gap-2.5 rounded-[10px] border border-white/20 bg-white/10 px-2.5 py-2 text-left ${
            single ? 'cursor-default' : 'hover:bg-white/15'
          } ${collapsed ? 'justify-center' : ''}`}
        >
          <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-md bg-white/20 text-white">
            <ActiveIcon size={13} aria-hidden />
          </span>
          {!collapsed && (
            <>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[12.5px] font-semibold text-white">{identity.title}</span>
                {identity.subtitle && (
                  <span className="block truncate text-[10.5px] font-normal text-white/70">{identity.subtitle}</span>
                )}
              </span>
              {!single && <ChevronsUpDown size={13} className="shrink-0 text-white/60" aria-hidden />}
            </>
          )}
        </button>
        {menu}
      </div>
    )
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => !single && setOpen((o) => !o)}
        aria-haspopup={single ? undefined : 'menu'}
        aria-expanded={single ? undefined : open}
        disabled={single}
        className={`flex shrink-0 items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm font-medium transition-colors ${
          single ? 'cursor-default' : 'hover:bg-white/10'
        } ${KIND_CHIP[active.kind]}`}
        title={single ? 'Personal workspace' : 'Switch workspace'}
        /* The name and the role are two spans so a long name can truncate
           without eating the role. That splits the accessible name in two, so
           it is stated once here, whole. */
        aria-label={chipLabel}
      >
        {KIND_SWATCH[active.kind] && (
          <span className={`h-2.5 w-2.5 shrink-0 rounded-[3px] ${KIND_SWATCH[active.kind]}`} aria-hidden />
        )}
        <ActiveIcon size={16} aria-hidden />
        {/* The NAME truncates; the role never does. A long organisation name
            used to eat the "— Owner" suffix the figures always show, so the
            chip said which workspace you were in but not what you were in it
            as — which is the half that decides what the screen lets you do. */}
        <span className="max-w-[16rem] truncate">{active.name}</span>
        {chipRole && <span className="shrink-0 whitespace-nowrap">{` \u2014 ${chipRole}`}</span>}
        {!single && <ChevronDown size={15} aria-hidden className={open ? 'rotate-180 transition-transform' : 'transition-transform'} />}
      </button>
      {menu}
    </div>
  )
}
